import { z } from "zod";
import {
  coachMemoryCategorySchema,
  coachMemorySourceTypeSchema,
  type CoachMemoryCategory,
} from "./coach-memory";

// TES-1237 — AI coach drafts under Emelie's narrowed contract (A-01
// "SLUTLIGT ÖVERORDNAT BESLUT — AI-STÖD OCH PATIENTMINNE FÖR COACH", Drive
// 2026-10-10). QA only: every route and behaviour here sits behind
// AI_COACH_DRAFTS_ENABLED, which prod does not set.
//
// What the model may receive is decided by code, not by a prompt (A-01 §6:
// "Det får inte vara beroende enbart av en instruktion till språkmodellen"):
//
//   - a rotating, random session id — never Ella's patient id;
//   - a minimal selection of structured Patientminne rows in the areas below;
//   - the CURRENT patient message, and only once the sanitiser has removed
//     identifiers and the medical stop has found nothing medical.
//
// Everything else — chat history, original form answers, journal, lab
// results, prescriptions, medical status — has no path to the model.

// The Patientminne areas a draft may read: A-01 section 2's list (quoted)
// mapped onto the stored areas.
//
//   "mål och motivation"                     goal, motivation
//   "önskat slags stöd och
//    kommunikationspreferenser"              desired_support, preference
//   "vanor och rutiner"                      habit_routine
//   "viktigaste hinder och återkommande
//    icke-medicinska mönster"                challenge, recurring_pattern
//   "vad patienten tidigare har provat"      previous_attempt
//   "vad som har fungerat eller inte
//    fungerat"                               worked, did_not_work
//   "överenskommelser och aktuellt fokus"    agreement (+ class current_focus)
//   "frågor att följa upp"                   follow_up_question, open_follow_up
//   "relevanta icke-medicinska förändringar
//    sedan senaste kontakten", "tillfällig
//    praktisk kontext"                       change_since_contact
//                                            (+ class temporary_context)
//
// `relevant_consideration` ("Relevant hänsyn") is deliberately absent: it is
// where sensitive background minimised from a doctor's handoff lives, and
// A-01 keeps medical and handoff content out of the AI input.
export const AI_COACH_DRAFT_MEMORY_CATEGORIES = [
  "goal",
  "motivation",
  "desired_support",
  "preference",
  "habit_routine",
  "challenge",
  "recurring_pattern",
  "previous_attempt",
  "worked",
  "did_not_work",
  "agreement",
  "follow_up_question",
  "open_follow_up",
  "change_since_contact",
] as const satisfies readonly CoachMemoryCategory[];
export type AiCoachDraftMemoryCategory =
  (typeof AI_COACH_DRAFT_MEMORY_CATEGORIES)[number];

export function isAiCoachDraftMemoryCategory(
  category: string,
): category is AiCoachDraftMemoryCategory {
  return (AI_COACH_DRAFT_MEMORY_CATEGORIES as readonly string[]).includes(
    category,
  );
}

// The coach cases a draft can be made for.
//
//   patient_message  she wrote to the coach; the draft answers her CURRENT
//                    message (and only that one, sanitised)
//   welcome          "Välkomna ny patient": no patient text at all
//   recontact        "Behöver återkontakt": no patient text at all
//   follow_up        the coach's own planned follow-up: no patient text
//
// A weekly check-in is deliberately not here: its answers are original form
// answers, which A-01 §6 keeps from the provider.
export const AI_COACH_DRAFT_CASE_KINDS = [
  "patient_message",
  "welcome",
  "recontact",
  "follow_up",
] as const;
export const aiCoachDraftCaseKindSchema = z.enum(AI_COACH_DRAFT_CASE_KINDS);
export type AiCoachDraftCaseKind = z.infer<typeof aiCoachDraftCaseKindSchema>;

// Why no draft was made. Every value is fail-closed: the coach writes
// herself, and for the first two she uses the medical escalation.
//
//   medical          the message names something medical (symptom, side
//                    effect, medication, dose, lab result, diagnosis, …)
//   uncertain        health-adjacent wording the rules cannot clear — treated
//                    exactly like `medical`, never as "probably fine"
//   unsanitisable    identifiers, combined identity clues, prompt injection
//                    or a text too long to be "the current message"
export const AI_COACH_DRAFT_STOP_REASONS = [
  "medical",
  "uncertain",
  "unsanitisable",
] as const;
export const aiCoachDraftStopReasonSchema = z.enum(AI_COACH_DRAFT_STOP_REASONS);
export type AiCoachDraftStopReason = z.infer<
  typeof aiCoachDraftStopReasonSchema
>;

// What a Patientminne text may not contain under this contract (A-01 §3),
// as the validator reports it. The text itself is never echoed back.
export const STRUCTURED_MEMORY_VIOLATIONS = [
  "identifier",
  "medical",
  "uncertain",
  "too_long",
] as const;
export const structuredMemoryViolationSchema = z.enum(
  STRUCTURED_MEMORY_VIOLATIONS,
);
export type StructuredMemoryViolation = z.infer<
  typeof structuredMemoryViolationSchema
>;

// A structured memory row is short: A-01 §3 rules out "längre fritext". The
// manual Patientminne keeps its own 4000-character limit; a longer row is
// simply never handed to a draft.
export const AI_COACH_DRAFT_MEMORY_MAX_CHARS = 280;
// "The current message", not a letter: longer than this and it is not sent.
export const AI_COACH_DRAFT_MESSAGE_MAX_CHARS = 1000;
// At most this many memory rows go into one draft (minimal selection).
export const AI_COACH_DRAFT_MEMORY_MAX_ROWS = 8;

// The label above every draft is COACH_DRAFT_LABEL (work-queue.ts), the same
// A-01 wording the welcome draft already carries.

// ---------------------------------------------------------------------------
// S2 — the draft endpoint
// ---------------------------------------------------------------------------

// What the coach asks for. A patient-message case names the message; the
// server checks it is her newest message in the coach chat (the CURRENT one)
// and never reads any other.
//
// TES-1352 — `excludeMemoryIds` is "Använd inte denna uppgift": the coach
// asks for a new draft without those Patientminne entries. The ids only
// narrow what the server would have selected anyway; they never widen it.
export const AI_COACH_DRAFT_EXCLUDE_MAX = 50;
export const aiCoachDraftRequestSchema = z
  .object({
    caseKind: aiCoachDraftCaseKindSchema,
    messageId: z.string().uuid().optional(),
    excludeMemoryIds: z
      .array(z.string().uuid())
      .max(AI_COACH_DRAFT_EXCLUDE_MAX)
      .optional(),
  })
  .refine((v) => (v.caseKind === "patient_message") === Boolean(v.messageId), {
    message: "message_id_iff_patient_message",
    path: ["messageId"],
  });
export type AiCoachDraftRequest = z.infer<typeof aiCoachDraftRequestSchema>;

// A suggested structured memory entry. Not saved until the coach confirms it
// (A-01 §5), and validated again when she does.
export const aiCoachDraftMemorySuggestionSchema = z.object({
  category: z.enum(AI_COACH_DRAFT_MEMORY_CATEGORIES),
  body: z.string().trim().min(1).max(AI_COACH_DRAFT_MEMORY_MAX_CHARS),
});
export type AiCoachDraftMemorySuggestion = z.infer<
  typeof aiCoachDraftMemorySuggestionSchema
>;

// A-01 §11: "Varje AI-utkast visar vilka minnesposter som användes."
// TES-1352 (Emelie 2026-10-14, §2) — the categories alone did not say what
// the draft rested on. Each entry the model was given now comes back with
// its actual text, its source and its dates, read from the same coach
// Patientminne read the draft used:
//
//   observedAt  "källdatum": when it was said or observed
//   updatedAt   "senast uppdaterad": when this version was written (a
//               correction is a new row, so its createdAt is the update)
//
// "Used" means "given to the model": the model does not report which of
// the entries it leaned on, so the list never claims less than it was given.
export const aiCoachDraftMemoryUsedSchema = z.object({
  id: z.string(),
  category: coachMemoryCategorySchema,
  memoryClass: z.string(),
  body: z.string(),
  agreement: z.string().nullable(),
  sourceType: coachMemorySourceTypeSchema,
  sourceTemplateKey: z.string().nullable(),
  observedAt: z.string(),
  updatedAt: z.string(),
});
export type AiCoachDraftMemoryUsed = z.infer<
  typeof aiCoachDraftMemoryUsedSchema
>;

export const aiCoachDraftSchema = z.object({
  id: z.string(),
  generatedAt: z.string(),
  // The editable reply, the optional single follow-up question and the one
  // small NON-medical next step. None of them is sent or stored as anything
  // but this suggestion until the coach acts.
  //
  // TES-1351 (Emelie 2026-10-14, §1) — one complete draft. A follow-up the
  // model marks as needed for the answer to be complete is already at the end
  // of `reply` (`followUpIncluded`); `followUpQuestion` is only ever the one
  // OPTIONAL follow-up, offered separately.
  reply: z.string(),
  followUpQuestion: z.string().nullable(),
  followUpIncluded: z.boolean(),
  nextStep: z.string().nullable(),
  memorySuggestion: aiCoachDraftMemorySuggestionSchema.nullable(),
  memoryUsed: z.array(aiCoachDraftMemoryUsedSchema),
  // Rows that exist but were kept from the model (identifiers, medical
  // wording, handoff, area not allowed, over the row limit …).
  memoryExcludedCount: z.number().int().nonnegative(),
  // TES-1352 — the patient's current message exactly as the model received
  // it, after the sanitiser; null for a case without a patient message.
  sanitisedMessage: z.string().nullable(),
});
export type AiCoachDraft = z.infer<typeof aiCoachDraftSchema>;

// The decided escalation path for a medical stop (A-01, "Medicinsk
// information i coachytan"): the coach uses "Skicka till läkare" on the
// message, which opens a medical review case. `acute` also points her to the
// safety route.
export const aiCoachDraftResultSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("draft"), draft: aiCoachDraftSchema }),
  z.object({
    status: z.literal("stopped"),
    reason: aiCoachDraftStopReasonSchema,
    acute: z.boolean(),
    escalation: z
      .object({
        action: z.literal("send_to_doctor"),
        messageId: z.string(),
      })
      .nullable(),
  }),
]);
export type AiCoachDraftResult = z.infer<typeof aiCoachDraftResultSchema>;

// What the coach did with a draft. `sent`: she sent a message that began as
// this draft (edited or not). `discarded`: she threw it away.
// `memory_rejected`: she declined the suggested memory entry.
export const AI_COACH_DRAFT_DECISIONS = [
  "sent",
  "discarded",
  "memory_rejected",
] as const;
export const aiCoachDraftDecisionSchema = z.object({
  decision: z.enum(AI_COACH_DRAFT_DECISIONS),
  edited: z.boolean().optional(),
});
export type AiCoachDraftDecision = z.infer<typeof aiCoachDraftDecisionSchema>;

export const AI_COACH_DRAFT_ERRORS = {
  disabled: "ai_coach_drafts_disabled",
  messageNotFound: "ai_coach_draft_message_not_found",
  messageNotCurrent: "ai_coach_draft_message_not_current",
  draftNotFound: "ai_coach_draft_not_found",
  outputRejected: "ai_coach_draft_output_rejected",
  providerUnavailable: "ai_assist_provider_unavailable",
  memoryForbidden: "coach_memory_forbidden_content",
} as const;
